/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography, useTheme } from '@material-ui/core';
import {
  Background,
  Controls,
  MarkerType,
  MiniMap,
  ReactFlow,
  useEdgesState,
  useNodesState,
  type Edge,
  type Node,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Change,
  hasTraefikLockdown,
  IngressEdge,
  isTrulyOpen,
  NetworkPolicyGraph,
  policyState,
  ServiceNode,
} from './types';
import { ServiceFlowNode, ServiceNodeData } from './ServiceFlowNode';
import { IngressFlowEdge, IngressEdgeData } from './IngressFlowEdge';
import { runElkLayout } from './elkLayout';

// Fallbacks for the measure-pass TIMEOUT path only: ELK is normally fed the
// DOM-measured size of every node (see the measure pass below), but a node
// that never reports a measurement must not hang the canvas hidden forever,
// so after the timeout the layout proceeds with these instead.
const NODE_W = 200;
const NODE_H = 72;
/** How long a measure pass may take before the fallback-size escape hatch. */
const MEASURE_TIMEOUT_MS = 2000;

type MeasuredDim = { width: number; height: number; sig: string };

// Everything ServiceFlowNode renders that can change a node's SIZE for a
// fixed id: the locked/staged icon vs the open-state chip (staging or
// unstaging an enablePolicy swaps a ~70px chip for a 16px icon), the
// allow-all vs no-peers label, the Traefik dot, and the fetch-error icon.
// A signature mismatch invalidates the cached measurement so the node is
// re-measured instead of ELK being fed a stale (possibly smaller) size.
const sizeSignature = (data: ServiceNodeData): string => {
  const svc = data.service;
  return [
    data.ghost ? 'ghost' : '',
    data.stagedEnable ? 'staged' : '',
    policyState(svc),
    isTrulyOpen(svc) ? 'open' : '',
    hasTraefikLockdown(svc) ? 'traefik' : '',
    svc.fetchError ? 'err' : '',
  ].join('|');
};

const hasFreshMeasurement = (
  cache: Map<string, MeasuredDim>,
  n: Node<ServiceNodeData>,
): boolean => {
  const c = cache.get(n.id);
  return !!c && c.sig === sizeSignature(n.data);
};

const nodeTypes = { service: ServiceFlowNode };
const edgeTypes = { ingress: IngressFlowEdge };

const ghostNode = (namespace: string, name: string): ServiceNode => ({
  name,
  entityRef: '',
  team: null,
  namespace,
  repo: null,
  envs: [],
  policyEnabled: {},
  autoLockdownActive: {},
  chartRev: {},
  exposure: {},
});

type Props = {
  graph: NetworkPolicyGraph;
  stagedChanges: Change[];
  teamFilter: string | null;
  selected: string | null;
  onSelect: (name: string | null) => void;
};

export const NetworkTopologyGraph = ({
  graph,
  stagedChanges,
  teamFilter,
  selected,
  onSelect,
}: Props) => {
  const computed = useMemo(() => {
    const byName = new Map(graph.nodes.map(n => [n.name, n]));

    // Real edges already present in the graph, keyed the same way a staged
    // addCaller/enablePolicy entry would be. Used to skip staging a
    // redundant parallel edge when the caller can already reach the callee.
    const realEdgeKeys = new Set(
      graph.edges.map(e => `${e.callerNamespace}|${e.callerName}|${e.callee}`),
    );

    // Staged overlay -> extra edges (adds) and edge markers (removes). Keyed
    // by caller|callee so staging the same caller twice (e.g. once via
    // addCaller and again via enablePolicy, or the same op staged twice)
    // dedupes to one edge instead of emitting duplicate React Flow ids.
    const stagedAddsMap = new Map<string, IngressEdge>();
    const stagedRemovals = new Set<string>();
    const stagedEnable = new Set<string>();
    // Edges a staged promoteEnv will extend to its target env (present in
    // fromEnv, absent in toEnv). Keyed like stagedRemovals.
    const stagedPromotes = new Set<string>();
    const stageAdd = (
      service: string,
      caller: { name: string; namespace: string; ports?: IngressEdge['ports'] },
      envs: string[] | 'all',
    ) => {
      const key = `${caller.namespace}|${caller.name}|${service}`;
      if (realEdgeKeys.has(key)) return; // already a real edge; nothing to overlay
      stagedAddsMap.set(key, {
        id: `staged|${key}`,
        caller: byName.has(caller.name) ? caller.name : null,
        callerName: caller.name,
        callerNamespace: caller.namespace,
        callee: service,
        ports: caller.ports ?? null,
        portsDiffer: false,
        envs: envs === 'all' ? [] : envs,
      });
    };
    for (const c of stagedChanges) {
      if (c.op === 'addCaller') {
        stageAdd(c.service, c.caller, c.envs);
      } else if (c.op === 'enablePolicy') {
        stagedEnable.add(c.service);
        for (const caller of c.callers) {
          stageAdd(c.service, caller, c.envs);
        }
      } else if (c.op === 'removeCaller') {
        stagedRemovals.add(`${c.callerNamespace}|${c.callerName}|${c.service}`);
      } else if (c.op === 'promoteEnv') {
        // A promote carries no caller list of its own; the callers it will
        // sync are exactly the real edges present in fromEnv but not toEnv.
        // Mark those so the staged change is visible in the graph and not
        // only in the staged-changes panel.
        for (const e of graph.edges) {
          if (e.callee !== c.service) continue;
          if (e.envs.includes(c.fromEnv) && !e.envs.includes(c.toEnv)) {
            stagedPromotes.add(`${e.callerNamespace}|${e.callerName}|${e.callee}`);
          }
        }
      }
    }
    const stagedAdds = [...stagedAddsMap.values()];

    const allEdges = [...graph.edges, ...stagedAdds];

    // Ghost nodes for callers that aren't catalog services. Keyed on
    // namespace|name (not namespace alone) so distinct non-catalog callers
    // sharing a namespace (e.g. two different batch jobs) stay separate
    // nodes instead of collapsing onto whichever one was seen first.
    const ghostKey = (namespace: string, name: string) => `${namespace}|${name}`;
    const ghosts = new Map<string, ServiceNode>();
    for (const e of allEdges) {
      if (!e.caller) {
        const key = ghostKey(e.callerNamespace, e.callerName);
        if (!ghosts.has(key)) ghosts.set(key, ghostNode(e.callerNamespace, e.callerName));
      }
    }

    const visible = (n: ServiceNode) => !teamFilter || n.team === teamFilter;
    const visibleNames = new Set(graph.nodes.filter(visible).map(n => n.name));
    // Keep filtered-out nodes that still touch a visible edge, so the picture
    // stays truthful about cross-team traffic. This is a fixpoint (iterate
    // until nothing new is added), not a single pass: a node several hops
    // away from a visible one (a multi-hop cross-team chain) must be
    // included regardless of the order edges happen to be in.
    let expanded = true;
    while (expanded) {
      expanded = false;
      for (const e of allEdges) {
        const callerVisible = e.caller ? visibleNames.has(e.caller) : false;
        if (callerVisible && !visibleNames.has(e.callee)) {
          visibleNames.add(e.callee);
          expanded = true;
        }
        if (visibleNames.has(e.callee) && e.caller && !visibleNames.has(e.caller)) {
          visibleNames.add(e.caller);
          expanded = true;
        }
      }
    }

    const nodeId = (e: IngressEdge) =>
      e.caller ?? `ghost:${ghostKey(e.callerNamespace, e.callerName)}`;
    const shownEdges = allEdges.filter(
      e =>
        visibleNames.has(e.callee) ||
        (e.caller ? visibleNames.has(e.caller) : false),
    );
    const shownGhostIds = new Set(
      shownEdges
        .filter(e => !e.caller)
        .map(e => `ghost:${ghostKey(e.callerNamespace, e.callerName)}`),
    );

    const flowNodes: Node<ServiceNodeData>[] = [];
    const nodeIds = new Set<string>();
    for (const n of graph.nodes.filter(x => visibleNames.has(x.name))) {
      nodeIds.add(n.name);
      flowNodes.push({
        id: n.name,
        type: 'service',
        position: { x: 0, y: 0 },
        data: {
          service: n,
          stagedEnable: stagedEnable.has(n.name),
        },
      });
    }
    for (const [key, ghost] of ghosts) {
      const id = `ghost:${key}`;
      if (!shownGhostIds.has(id)) continue;
      nodeIds.add(id);
      flowNodes.push({
        id,
        type: 'service',
        position: { x: 0, y: 0 },
        data: { service: ghost, ghost: true },
      });
    }

    const flowEdges: Edge<IngressEdgeData>[] = [];
    for (const e of shownEdges) {
      const source = nodeId(e);
      if (!nodeIds.has(source) || !nodeIds.has(e.callee)) continue;
      const callee = byName.get(e.callee);
      const partial =
        e.envs.length > 0 &&
        callee !== undefined &&
        e.envs.length < callee.envs.length;
      const edgeKey = `${e.callerNamespace}|${e.callerName}|${e.callee}`;
      let staged: 'add' | 'remove' | undefined;
      if (e.id.startsWith('staged|') || stagedPromotes.has(edgeKey)) {
        staged = 'add';
      } else if (stagedRemovals.has(edgeKey)) {
        staged = 'remove';
      }
      flowEdges.push({
        id: e.id,
        source,
        target: e.callee,
        type: 'ingress',
        markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14 },
        data: {
          ports: e.ports,
          portsDiffer: e.portsDiffer,
          envs: partial ? e.envs : [],
          partial,
          staged,
        },
      });
    }

    return { nodes: flowNodes, edges: flowEdges };
    // `selected` is intentionally excluded: it's pure UI highlight state and
    // must not trigger a relayout (which would also discard manual drags).
    // It's applied to the rendered nodes separately, below.
  }, [graph, stagedChanges, teamFilter]);

  // Controlled arrays alone drop React Flow's internal measurement updates
  // (nodes stay visibility:hidden); route them through useNodesState. Start
  // empty: the first commit happens only after ELK returns, so nodes never
  // flash stacked at the origin.
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<ServiceNodeData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge<IngressEdgeData>>([]);

  // The initial fit must follow the FIRST ELK result, not a fixed delay:
  // nodes arrive only after the lazy elkjs import + layout, which can lose a
  // race against any timer on a cold load. The instance lands here from
  // onInit; the layout effect fits once real positions are applied.
  const rfInstance = useRef<ReactFlowInstance<
    Node<ServiceNodeData>,
    Edge<IngressEdgeData>
  > | null>(null);
  const fitted = useRef(false);

  // The async engine adds a failure mode the old synchronous layout didn't
  // have: the lazy elkjs chunk can fail to load (redeploy invalidating asset
  // hashes, flaky network). Surface that instead of a silent blank canvas;
  // retry re-runs the effect and, with the import memo cleared on failure,
  // re-attempts the download.
  const [layoutError, setLayoutError] = useState<string | null>(null);
  const [layoutAttempt, setLayoutAttempt] = useState(0);

  // ELK must be told REAL node sizes: names and chips make a node much wider
  // than any fixed constant (svc-game-cloud-storage + "no peers" is ~300px,
  // not NODE_W), and every px of underestimate is a px ELK will let another
  // element occupy -- in prod that overlapped the packed no-peers components
  // and routed a corridor through svc-social-party's box. Nodes without a
  // FRESH size (unknown id, or a size-signature mismatch after content
  // changed) render hidden at the origin for one frame so React Flow can
  // measure the actual DOM; relayouts of an unchanged node set skip the
  // measure pass entirely.
  const measuredDims = useRef(new Map<string, MeasuredDim>());
  const pendingMeasure = useRef(false);
  // Set when a measure pass timed out: the next layout run proceeds with
  // fallback sizes instead of re-entering the measure pass (which would
  // otherwise loop hidden forever on a node that never measures).
  const measureTimedOut = useRef(false);
  const [measureTick, setMeasureTick] = useState(0);

  useEffect(() => {
    for (const n of nodes) {
      const m = n.measured;
      if (m?.width && m?.height) {
        measuredDims.current.set(n.id, {
          width: m.width,
          height: m.height,
          sig: sizeSignature(n.data),
        });
      }
    }
    if (
      pendingMeasure.current &&
      computed.nodes.every(n => hasFreshMeasurement(measuredDims.current, n))
    ) {
      pendingMeasure.current = false;
      setMeasureTick(t => t + 1);
    }
  }, [nodes, computed]);

  useEffect(() => {
    const unmeasured = computed.nodes.filter(
      n => !hasFreshMeasurement(measuredDims.current, n),
    );
    if (unmeasured.length > 0 && !measureTimedOut.current) {
      pendingMeasure.current = true;
      setNodes(
        computed.nodes.map(n => ({
          ...n,
          position: { x: 0, y: 0 },
          style: { visibility: 'hidden' as const },
        })),
      );
      setEdges([]);
      const timer = window.setTimeout(() => {
        if (pendingMeasure.current) {
          pendingMeasure.current = false;
          measureTimedOut.current = true;
          setMeasureTick(t => t + 1);
        }
      }, MEASURE_TIMEOUT_MS);
      return () => window.clearTimeout(timer);
    }
    let active = true;
    (async () => {
      let result;
      try {
        result = await runElkLayout({
          nodes: computed.nodes.map(n => {
            const dims = measuredDims.current.get(n.id);
            return {
              id: n.id,
              width: dims?.width ?? NODE_W,
              height: dims?.height ?? NODE_H,
            };
          }),
          edges: computed.edges.map(e => ({
            id: e.id,
            source: e.source,
            target: e.target,
          })),
        });
      } catch (e) {
        if (active) {
          // The error branch unmounts ReactFlow: forget the stale instance
          // and let the post-retry remount's onInit own the initial fit.
          fitted.current = false;
          rfInstance.current = null;
          // Never falsy: an empty/absent message would skip the error UI
          // and reproduce the silent blank canvas this path exists to avoid.
          setLayoutError((e as Error)?.message || 'unknown error');
        }
        return;
      }
      if (!active) return;
      setLayoutError(null);
      // A completed layout re-arms normal measuring for future changes.
      measureTimedOut.current = false;
      const { positions, routes } = result;
      // Nodes are not draggable: ELK's positions and routes are always the
      // truth, so both can be applied wholesale (no manual-drag positions to
      // preserve, no stale routes to fall back from).
      setNodes(
        computed.nodes.map(n => ({
          ...n,
          position: positions.get(n.id) ?? { x: 0, y: 0 },
        })),
      );
      setEdges(
        computed.edges.map(e => ({
          ...e,
          data: {
            ...(e.data as IngressEdgeData),
            points: routes.get(e.id),
          },
        })),
      );
      // First layout only: later structural changes (staging, filters) must
      // not yank a viewport the user has panned/zoomed. rAF lets the state
      // flush render before the fit measures node bounds. Only when the
      // flow is already mounted (instance present) -- after an error->retry
      // the flow remounts and its onInit performs the fit instead, so the
      // fit never races the remount.
      if (!fitted.current && computed.nodes.length > 0 && rfInstance.current) {
        fitted.current = true;
        window.requestAnimationFrame(() => {
          rfInstance.current?.fitView({ maxZoom: 1 });
        });
      }
    })();
    return () => {
      active = false;
    };
  }, [computed, layoutAttempt, measureTick, setNodes, setEdges]);

  // Merge the `selected` highlight onto the current nodes without touching
  // position or triggering the relayout effect above. Nodes whose highlight
  // state is already correct keep their object identity so the memoized
  // ServiceFlowNode skips them -- a click re-renders two nodes, not all.
  const displayNodes = useMemo(
    () =>
      nodes.map(n => {
        if (n.id.startsWith('ghost:')) return n;
        const isSelected = selected === n.id;
        return (n.data.selected ?? false) === isSelected
          ? n
          : { ...n, data: { ...n.data, selected: isSelected } };
      }),
    [nodes, selected],
  );

  // Follow the active Backstage palette: React Flow's chrome (zoom/fit
  // controls, minimap) otherwise renders light-mode white and clashes with
  // the dark theme most of the org runs.
  const theme = useTheme();

  if (layoutError) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="body2" color="error" gutterBottom>
          Graph layout failed: {layoutError}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setLayoutAttempt(a => a + 1)}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <ReactFlow
      nodes={displayNodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      colorMode={theme.palette.type === 'dark' ? 'dark' : 'light'}
      fitView
      minZoom={0.2}
      proOptions={{ hideAttribution: true }}
      onInit={instance => {
        rfInstance.current = instance;
        // A mount that already has nodes (remount after error->retry, or
        // layout beating React Flow's init) owns the initial fit here.
        // Never during a measure pass: those nodes are hidden at the origin
        // and fitting them would lock in a nonsense viewport.
        if (!fitted.current && nodes.length > 0 && !pendingMeasure.current) {
          fitted.current = true;
          window.requestAnimationFrame(() => instance.fitView({ maxZoom: 1 }));
        }
      }}
      onNodeClick={(_, node) => {
        if (!node.id.startsWith('ghost:')) onSelect(node.id);
      }}
      onPaneClick={() => onSelect(null)}
      // Dragging is off by design: edges render static ELK routes, and a
      // moved node would leave its edges pointing at empty space. ELK's
      // layout replaces manual arranging.
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable
    >
      <Background gap={24} />
      <Controls showInteractive={false} />
      <MiniMap
        pannable
        zoomable
        // xyflow's dark defaults paint minimap nodes #2b2b2b on a #141414
        // background (~1.3:1 contrast - functionally invisible), so give
        // them a readable fill in dark mode; light mode keeps the library
        // defaults, byte-identical to the pre-colorMode rendering.
        {...(theme.palette.type === 'dark'
          ? {
              nodeColor: theme.palette.grey[600],
              nodeStrokeColor: theme.palette.grey[400],
            }
          : {})}
      />
    </ReactFlow>
  );
};
